import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type { BarcodeSearchResponse, Product } from '../types';

// Search parameters for barcode lookup
export interface BarcodeSearchParams {
  barcode: string;
  locationId?: string;
}

// Link barcode request payload
export interface LinkBarcodePayload {
  productId: string;
  barcode: string;
}

// API functions for barcode product search
export const barcodeProductApi = {
  /**
   * Search for a product by barcode
   * @param params - barcode and optional locationId for inventory lookup
   * @returns BarcodeSearchResponse with found status and product data
   */
  searchByBarcode: async (params: BarcodeSearchParams): Promise<BarcodeSearchResponse> => {
    const { barcode, locationId } = params;
    const response = await axiosInstance.get<BarcodeSearchResponse>(
      `/products/barcode/${encodeURIComponent(barcode.trim())}`,
      { params: locationId ? { locationId } : undefined }
    );
    return response.data;
  },

  /**
   * Link a barcode to an existing product
   * @param payload - productId and barcode to link
   * @returns Updated product
   */
  linkBarcode: async (payload: LinkBarcodePayload): Promise<Product> => {
    const { productId, barcode } = payload;
    const response = await axiosInstance.patch<Product>(
      `/products/${productId}/barcode`,
      { barcode }
    );
    return response.data;
  },
};

/**
 * Hook for searching products by barcode using TanStack Query
 *
 * @param params - BarcodeSearchParams with barcode and optional locationId
 * @param options - Additional options including enabled flag
 * @returns Query result with BarcodeSearchResponse data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useBarcodeProductSearch({
 *   barcode: scannedBarcode,
 *   locationId: selectedLocationId,
 * });
 *
 * if (data?.found && data?.product) {
 *   // Product found, add to cart or display
 * }
 * ```
 */
export const useBarcodeProductSearch = (
  params: BarcodeSearchParams,
  options?: { enabled?: boolean }
) => {
  const hasBarcode = !!params.barcode && params.barcode.trim().length > 0;

  return useQuery({
    queryKey: ['products', 'barcode', params.barcode, params.locationId],
    queryFn: () => barcodeProductApi.searchByBarcode(params),
    enabled: options?.enabled !== false && hasBarcode,
    // Don't retry on 404 (product not found is expected behavior)
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
    // Cache for 30 seconds - barcodes don't change frequently
    staleTime: 30 * 1000,
  });
};

/**
 * Hook for linking a barcode to an existing product
 *
 * @returns Mutation for linking barcode with success/error handling
 *
 * @example
 * ```tsx
 * const linkBarcodeMutation = useLinkBarcode();
 *
 * const handleLinkBarcode = async (productId: string, barcode: string) => {
 *   try {
 *     const product = await linkBarcodeMutation.mutateAsync({ productId, barcode });
 *     // Success - product now has the barcode linked
 *   } catch (error) {
 *     // Handle error (e.g., duplicate barcode)
 *   }
 * };
 * ```
 */
export const useLinkBarcode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: barcodeProductApi.linkBarcode,
    onSuccess: (updatedProduct, variables) => {
      // Invalidate barcode search queries to reflect the new link
      queryClient.invalidateQueries({ queryKey: ['products', 'barcode', variables.barcode] });

      // Invalidate product queries to update product data
      queryClient.invalidateQueries({ queryKey: ['products', updatedProduct.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'search'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

/**
 * Utility function to perform barcode search imperatively
 * Useful for one-off searches without hook state management
 *
 * @param barcode - The barcode to search for
 * @param locationId - Optional location ID for inventory lookup
 * @returns Promise<BarcodeSearchResponse>
 */
export const searchProductByBarcode = async (
  barcode: string,
  locationId?: string
): Promise<BarcodeSearchResponse> => {
  return barcodeProductApi.searchByBarcode({ barcode, locationId });
};

export default useBarcodeProductSearch;
